if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();

      if (Shopify.designMode) {
        setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);

      if (this.resizeInitHandler) {
        window.removeEventListener('on:debounced-resize', this.resizeInitHandler);
      }

      if (this.zoomInitHandler) {
        window.removeEventListener('on:debounced-resize', this.zoomInitHandler);
      }

      // Clean up the MutationObserver that guards against external script hiding
      if (this._groupGuardObserver) {
        this._groupGuardObserver.disconnect();
        this._groupGuardObserver = null;
      }
    }

    init() {
      // Defensive: remove any instance-level override of setActiveMediaGroup
      // that external scripts (e.g. Shopify apps) may have injected as a no-op.
      // This ensures the prototype method always runs for media grouping.
      if (this.hasOwnProperty('setActiveMediaGroup')) {
        delete this.setActiveMediaGroup;
      }
      if (this.hasOwnProperty('_origSetActiveMediaGroup')) {
        delete this._origSetActiveMediaGroup;
      }

      // Lock setActiveMediaGroup so external scripts cannot replace it with a no-op.
      // Any attempt to assign to this.setActiveMediaGroup will be silently ignored.
      Object.defineProperty(this, 'setActiveMediaGroup', {
        get: () => MediaGallery.prototype.setActiveMediaGroup,
        set: () => {},  // silently ignore external overrides
        configurable: false
      });

      this.section = this.closest('.js-product');
      this.noSelectedVariant = this.hasAttribute('data-no-selected-variant');
      this.mediaGroupingEnabled = this.hasAttribute('data-media-grouping-enabled')
        && this.getMediaGroupData();

      // Detect if images have compound ALT tags (e.g. [Grey | Single])
      // enabling more specific grouping by multiple options (colour + size)
      this._compoundGrouping = false;
      if (this.mediaGroupingEnabled) {
        const tempItems = this.querySelectorAll('.media-viewer__item');
        for (const item of tempItems) {
          const img = item.querySelector('img');
          if (img && img.alt) {
            const m = img.alt.match(/^\[([^\]]+)\]/);
            if (m && m[1].includes('|')) {
              this._compoundGrouping = true;
              break;
            }
          }
        }
      }

      this.stackedScroll = this.dataset.stackedScroll;
      this.stackedUnderline = this.dataset.stackedUnderline === 'true' && !this.mediaGroupingEnabled;
      this.isFeatured = this.dataset.isFeatured === 'true';
      this.viewer = this.querySelector('.media-viewer');
      this.thumbs = this.querySelector('.media-thumbs');
      this.thumbsItems = this.querySelectorAll('.media-thumbs__item');
      this.controls = this.querySelector('.media-ctrl');
      this.prevBtn = this.querySelector('.media-ctrl__btn[name="prev"]');
      this.nextBtn = this.querySelector('.media-ctrl__btn[name="next"]');
      this.counterCurrent = this.querySelector('.media-ctrl__current-item');
      this.counterTotal = this.querySelector('.media-ctrl__total-items');
      this.liveRegion = this.querySelector('.media-gallery__status');
      this.zoomLinks = this.querySelectorAll('.js-zoom-link');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.xrButton = this.querySelector('.media-xr-button');

      if (this.hasAttribute('data-zoom-enabled')) {
        this.galleryModal = this.querySelector('.js-media-zoom-template').content.firstElementChild.cloneNode(true);
      }

      if (this.mediaGroupingEnabled && !this.noSelectedVariant) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        this.resizeInitHandler = this.resizeInitHandler || this.initGallery.bind(this);
        window.addEventListener('on:debounced-resize', this.resizeInitHandler);
        this.setVisibleItems();
        this.previousMediaItem = this.querySelector('.media-viewer__item.is-current-variant');
        setTimeout(() => this.customSetActiveMedia(this.previousMediaItem, this.stackedScroll === 'always'), 200);
      } else {
        this.initGallery();
      }

      if (this.zoomLinks) {
        if (this.dataset.zoomTrigger === 'hover') {
          this.triggerZoomInit();
        } else {
          this.zoomLinks.forEach((zoomLink) => {
            zoomLink.addEventListener('click', (evt) => {
              this.triggerZoomInit();
              evt.preventDefault();
            });
          });
        }
      }

      this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));

      // Safety net: external scripts (Shopify apps) may hide all gallery items
      // after init and after every variant change (section re-render). Instead of
      // fixed timeouts, use a MutationObserver to permanently guard the current
      // group's items against being hidden by external scripts.
      if (this.mediaGroupingEnabled && this.currentGroup) {
        this._startGroupGuard();
      }
    }

    /**
     * Starts a MutationObserver that watches for external scripts hiding
     * gallery items. When items in the current group are set to display:none
     * by external code, this immediately reverses it.
     */
    _startGroupGuard() {
      // Clean up any previous observer
      if (this._groupGuardObserver) {
        this._groupGuardObserver.disconnect();
      }

      const self = this;
      let recovering = false;

      const recoverVisibility = () => {
        if (recovering) return;
        recovering = true;
        const grp = self.currentGroup;
        if (!grp) { recovering = false; return; }
        let fixed = false;
        grp.items.forEach((item) => {
          if (item.main.style.display === 'none') {
            item.main.style.display = '';
            fixed = true;
          }
          if (item.thumb && item.thumb.style.display === 'none') {
            item.thumb.style.display = '';
          }
        });
        if (fixed) {
          self.setVisibleItems();
          if (self.visibleItems.length > 1 && !self.viewerItemOffset) {
            self.initGallery();
          }
        }
        recovering = false;
      };

      // Immediate recovery pass + early timeouts for fast first paint
      requestAnimationFrame(recoverVisibility);
      setTimeout(recoverVisibility, 150);
      setTimeout(recoverVisibility, 500);

      // MutationObserver: watches for style attribute changes on gallery items.
      // This catches the external script regardless of when it runs.
      this._groupGuardObserver = new MutationObserver((mutations) => {
        const grp = self.currentGroup;
        if (!grp) return;
        let needsRecovery = false;
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
            const el = mutation.target;
            // Check if this element is a main item in the current group that was hidden
            if (el.style.display === 'none') {
              for (const item of grp.items) {
                if (item.main === el) {
                  needsRecovery = true;
                  break;
                }
              }
            }
            if (needsRecovery) break;
          }
        }
        if (needsRecovery) {
          recoverVisibility();
        }
      });

      // Observe all viewer items for style attribute changes
      this.viewerItems.forEach((item) => {
        this._groupGuardObserver.observe(item, { attributes: true, attributeFilter: ['style'] });
      });
      // Also observe thumbnail items
      this.thumbsItems.forEach((item) => {
        this._groupGuardObserver.observe(item, { attributes: true, attributeFilter: ['style'] });
      });
    }

    triggerZoomInit() {
      this.zoomInitHandler = this.zoomInitHandler || this.initZoom.bind(this);
      this.zoomEventListener = this.zoomEventListener || this.handleZoomMouseMove.bind(this);
      window.addEventListener('on:debounced-resize', this.zoomInitHandler);
      this.initZoom();
      this.zoomLinks.forEach((zoomLink) => {
        zoomLink.addEventListener('click', (evt) => {
          evt.preventDefault();
        });
      });
    }

    /**
     * Handle a change in variant on the page.
     * @param {Event} evt - variant change event dispatched by variant-picker
     */
    onVariantChange(evt) {
      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        const variantMedia = this.viewer.querySelector(
          `[data-media-id="${evt.detail.variant.featured_media.id}"]`
        );
        if (variantMedia) {
          this.customSetActiveMedia(variantMedia, true);
        }
      }
    }

    /**
     * Gets the media group from currently selected variant options.
     * @returns {?object}
     */
    getMediaGroupFromOptionSelectors() {
      const optionSelectors = this.section.querySelectorAll('.option-selector');
      if (optionSelectors.length > this.getMediaGroupData().groupOptionIndex) {
        const selector = optionSelectors[this.getMediaGroupData().groupOptionIndex];
        if (selector.dataset.selectorType === 'dropdown') {
          return selector.querySelector('.custom-select__btn').textContent.trim();
        }
        return selector.querySelector('input:checked').value;
      }
      return null;
    }

    /**
     * Gets the variant media associations for a product.
     * @returns {?object}
     */
    getMediaGroupData() {
      if (typeof this.variantMediaData === 'undefined') {
        const dataEl = this.querySelector('.js-data-variant-media');
        this.variantMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
      }

      return this.variantMediaData;
    }

    /**
     * Gets an object mapping media to groups, and the reverse
     * @returns {?object}
     */
    getMediaGroupMap() {
      if (!this.mediaGroupMap) {
        this.mediaGroupMap = {
          groups: {}
        };

        // set up grouping
        const variantMediaData = this.getMediaGroupData();
        let currentMediaOptionName = false;
        this.viewerItems = this.querySelectorAll('.media-viewer__item');
        this.viewerItems.forEach((item) => {
          for (let i = 0; i < variantMediaData.variantMedia.length; i += 1) {
            if (parseInt(item.dataset.mediaId, 10) === variantMediaData.variantMedia[i].mediaId) {
              if (currentMediaOptionName !== variantMediaData.variantMedia[i].option) {
                currentMediaOptionName = variantMediaData.variantMedia[i].option;
              }
            }
          }
          if (currentMediaOptionName) {
            if (!this.mediaGroupMap.groups[currentMediaOptionName]) {
              this.mediaGroupMap.groups[currentMediaOptionName] = {
                name: currentMediaOptionName,
                items: []
              };
            }
            const groupItem = { main: item };
            if (this.thumbs) {
              groupItem.thumb = this.thumbs.querySelector(
                `[data-media-id="${item.dataset.mediaId}"].media-thumbs__item`
              );
            }
            this.mediaGroupMap.groups[currentMediaOptionName].items.push(groupItem);
          }
        });

        // add helper
        this.mediaGroupMap.groupFromItem = (item) => {
          const groups = Object.keys(this.mediaGroupMap.groups);
          for (let i = 0; i < groups.length; i += 1) {
            const group = groups[i];
            for (let j = 0; j < this.mediaGroupMap.groups[group].items.length; j += 1) {
              if (this.mediaGroupMap.groups[group].items[j] === item) {
                return this.mediaGroupMap.groups[group];
              }
            }
          }
          return this.mediaGroupMap.groups[Object.keys(this.mediaGroupMap.groups)[0]];
        };
      }

      return this.mediaGroupMap;
    }

    /**
     * Parses the group values from an image's ALT tag bracket prefix.
     * E.g. "[Grey | Single] description..." returns ["Grey", "Single"]
     * @param {Element} item - A .media-viewer__item element
     * @returns {?string[]}
     */
    _parseAltGroupValues(item) {
      const img = item.querySelector('img');
      if (!img || !img.alt) return null;
      const match = img.alt.match(/^\[([^\]]+)\]/);
      if (!match) return null;
      return match[1].split('|').map(v => v.trim());
    }

    /**
     * Fuzzy-matches an ALT tag value against a selector option value.
     * Handles "Single" matching "Single (3ft)" or "Single 3ft".
     * @param {string} altVal - Value from ALT tag (e.g. "Single")
     * @param {string} optionVal - Value from option selector (e.g. "Single (3ft)")
     * @returns {boolean}
     */
    _altMatchesOption(altVal, optionVal) {
      const a = altVal.toLowerCase().trim();
      const o = optionVal.toLowerCase().trim();
      if (a === o) return true;
      return o.startsWith(a) || a.startsWith(o);
    }

    /**
     * Gets all currently selected option values from variant selectors.
     * @returns {string[]}
     */
    _getAllSelectedOptionValues() {
      const optionSelectors = this.section.querySelectorAll('.option-selector');
      const values = [];
      optionSelectors.forEach(selector => {
        let val;
        if (selector.dataset.selectorType === 'dropdown') {
          val = selector.querySelector('.custom-select__btn').textContent.trim();
        } else {
          const checked = selector.querySelector('input:checked');
          val = checked ? checked.value : null;
        }
        if (val) values.push(val);
      });
      return values;
    }

    /**
     * Show only images associated to the current variant
     * @param {string} groupName - optional - Group to show (uses this.currentItem if empty)
     */
    setActiveMediaGroup(groupName) {
      const mediaGroupMap = this.getMediaGroupMap();
      const baseGroup = groupName
        ? mediaGroupMap.groups[groupName]
        : mediaGroupMap.groupFromItem(this.currentItem);

      if (!baseGroup) {
        // No group found — show all items
        this.viewerItems.forEach((item) => { item.style.display = ''; });
        this.thumbsItems.forEach((item) => { item.style.display = ''; });
        return;
      }

      let effectiveGroup = baseGroup;
      let groupChanged = this.currentGroup !== baseGroup;

      // Compound grouping: further filter by ALL selected options (colour + size)
      if (this._compoundGrouping) {
        const selectedValues = this._getAllSelectedOptionValues();
        const compoundKey = selectedValues.map(v => v.toLowerCase().trim()).sort().join('|');

        if (this._currentCompoundKey === compoundKey) return;
        this._currentCompoundKey = compoundKey;
        groupChanged = true;

        // Filter the base group items by matching ALL ALT tag values
        const filteredItems = baseGroup.items.filter((item) => {
          const altVals = this._parseAltGroupValues(item.main);
          if (!altVals || altVals.length <= 1) return true; // keep single-value items
          return altVals.every((altVal) =>
            selectedValues.some((selVal) => this._altMatchesOption(altVal, selVal))
          );
        });

        if (filteredItems.length > 0) {
          effectiveGroup = { name: compoundKey, items: filteredItems };
        }
      }

      if (groupChanged) {
        this.currentGroup = effectiveGroup;

        this.viewerItems.forEach((item) => {
          item.style.display = 'none';
          item.classList.remove('media-viewer__item--single');
        });
        this.thumbsItems.forEach((item) => {
          item.style.display = 'none';
        });

        let currentItemIsVisible = false;
        effectiveGroup.items.forEach((item) => {
          item.main.style.display = '';
          if (item.thumb) {
            item.thumb.style.display = '';
          }
          if (item.main === this.currentItem) {
            currentItemIsVisible = true;
          }
        });
        this.setVisibleItems();

        // If current item is not in this group, set it as the active item
        if (!currentItemIsVisible) {
          this.customSetActiveMedia(effectiveGroup.items[0].main, true);
        }

        // Handle single images on stacked view
        if (effectiveGroup.items.length === 1) {
          effectiveGroup.items[0].main.classList.add('media-viewer__item--single');
        }

        // Restart the group guard for the new group's items
        if (this._groupGuardObserver) {
          this._startGroupGuard();
        }
      }
    }

    /**
     * Initialises the media gallery slider and associated controls.
     */
    initGallery() {
      this.setVisibleItems();
      if (this.visibleItems.length <= 1) return;

      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      this.currentIndex = this.viewerItemOffset ? Math.round(this.viewer.scrollLeft / this.viewerItemOffset) : 0;
      this.currentItem = this.visibleItems[this.currentIndex];
      this.addListeners();

      if (this.thumbs && this.currentItem) {
        this.currentThumb = this.thumbs.querySelector(
          `[data-media-id="${this.currentItem.dataset.mediaId}"]`
        );
      }

      if (!this.isFeatured && document.hasFocus()) {
        // Eager load the slider images for smooth UX
        this.viewer.querySelectorAll('.product-image[loading="lazy"]').forEach((img, index) => {
          setTimeout(() => {
            img.loading = 'eager';
          }, 500 * (index + 1));
        });
      }

      const currentItem = this.querySelector('.media-viewer__item.is-current-variant') || this.visibleItems[0];
      this.customSetActiveMedia(currentItem, true);
    }

    addListeners() {
      this.viewer.addEventListener('scroll', this.handleScroll.bind(this));
      if (this.controls) this.controls.addEventListener('click', this.handleNavClick.bind(this));
      if (this.thumbs) this.thumbs.addEventListener('click', this.handleThumbClick.bind(this));
      this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    /**
     * Initialized the zoom on hover for desktop
     */
    initZoom() {
      this.zoomLinks.forEach((el) => {
        const zoomWidth = Number(el.querySelector('.zoom-image').dataset.originalWidth || 0);
        const imageWidth = el.querySelector('.product-image').getBoundingClientRect().width;
        if (theme.mediaMatches.md && ((zoomWidth - 75) > (imageWidth))) {
          el.addEventListener('mousemove', this.zoomEventListener);
          el.classList.remove('pointer-events-none');
        } else {
          el.removeEventListener('mousemove', this.zoomEventListener);
          el.classList.add('pointer-events-none');
        }
      });
    }

    /**
     * Handles mouse move over a zoomable image
     * @param {?object} evt - Event object.
     */
    handleZoomMouseMove(evt) {
      if (this.hasAttribute('data-zoom-enabled') && !this.dataset.zoomTrigger && !evt.target.closest('.gallery-zoom-modal')) {
        return;
      }

      const hoverElem = evt.currentTarget;
      const zoomImage = hoverElem.querySelector('.js-zoom-image');

      // Download the zoom image if necessary
      if (zoomImage.dataset.src) {
        this.loadingSpinner.classList.remove('loading-spinner--out');

        const img = new Image();
        img.src = zoomImage.dataset.src;
        img.onload = () => {
          zoomImage.src = img.src;
          hoverElem.classList.remove('media--zoom-not-loaded');
          this.loadingSpinner.classList.add('loading-spinner--out');
        };
        zoomImage.removeAttribute('data-src');
      }

      try {
        const offsetX = evt.offsetX ? evt.offsetX : evt.touches[0].pageX;
        const offsetY = evt.offsetY ? evt.offsetY : evt.touches[0].pageY;
        const x = (offsetX / zoomImage.offsetWidth) * 100;
        const y = (offsetY / zoomImage.offsetHeight) * 100;
        zoomImage.style.objectPosition = `${x}% ${y}%`;
      } catch (err) {
        // Fail silently
      }
    }

    /**
     * Handles 'scroll' events on the main media container.
     */
    handleScroll() {
      const newIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);

      if (newIndex !== this.currentIndex) {
        const viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

        // If scroll wasn't caused by a resize event, update the active media.
        if (viewerItemOffset === this.viewerItemOffset) {
          this.customSetActiveMedia(this.visibleItems[newIndex], false);
        }
      }
    }

    /**
     * Handles 'click' events on the controls container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      if (!evt.target.matches('.media-ctrl__btn')) return;

      const itemToShow = evt.target === this.nextBtn
        ? this.visibleItems[this.currentIndex + 1]
        : this.visibleItems[this.currentIndex - 1];

      this.viewer.scrollTo({ left: itemToShow.offsetLeft, behavior: 'smooth' });
    }

    /**
     * Handles 'click' events on the thumbnails container.
     * @param {object} evt - Event object.
     */
    handleThumbClick(evt) {
      const thumb = evt.target.closest('[data-media-id]');
      if (!thumb) return;

      const itemToShow = this.querySelector(`[data-media-id="${thumb.dataset.mediaId}"]`);
      this.customSetActiveMedia(itemToShow);

      MediaGallery.playActiveMedia(itemToShow);
    }

    /**
     * Handles debounced 'resize' events on the window.
     */
    handleResize() {
      // Reset distance from leading edge of one slide to the next.
      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

      if (this.thumbs && this.currentThumb) {
        this.checkThumbVisibilty(this.currentThumb);
      }
    }

    /**
     * Stub for variant-picker calls. Listening to change event instead.
     */
    // eslint-disable-next-line class-methods-use-this
    setActiveMedia() {}

    /**
     * Sets the active media item.
     * @param {Element} mediaItem - Media element to set as active.
     * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
     */
    customSetActiveMedia(mediaItem, scrollToItem = true) {
      if (!mediaItem || mediaItem === this.currentItem) return;
      window.pauseAllMedia(this);
      this.currentItem = mediaItem;
      this.currentIndex = this.visibleItems.indexOf(this.currentItem);

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        // Update the active class and scroll to the active media
        if (this.stackedUnderline) {
          if (this.previousMediaItem) this.previousMediaItem.classList.remove('is-active');
          mediaItem.classList.add('is-active');
          this.previousMediaItem = mediaItem;
        }

        if (this.stackedScroll !== 'never') {
          const y = mediaItem.getBoundingClientRect().top
            + document.documentElement.scrollTop - 150;

          // If the element is far enough away to scroll to it
          if (Math.abs(y - document.documentElement.scrollTop) > 300) {
            window.scrollTo({
              top: y < 100 ? 0 : y,
              behavior: 'smooth'
            });
          }
        }
        return;
      }

      if (scrollToItem) this.viewer.scrollTo({ left: this.currentItem.offsetLeft });
      if (this.thumbs) this.setActiveThumb();

      if (this.controls) {
        if (this.prevBtn) {
          this.prevBtn.disabled = this.currentIndex === 0;
        }

        if (this.nextBtn) {
          this.nextBtn.disabled = this.currentIndex === this.visibleItems.length - 1;
        }

        if (this.counterCurrent) {
          this.counterCurrent.textContent = this.currentIndex + 1;
        }
      }

      this.announceLiveRegion(this.currentItem, this.currentIndex + 1);

      if (this.xrButton && mediaItem.dataset.mediaType === 'model') {
        this.xrButton.dataset.shopifyModel3dId = mediaItem.dataset.mediaId;
      }
    }

    /**
     * Sets the active thumbnail.
     */
    setActiveThumb() {
      this.currentThumb = this.thumbs.querySelector(
        `[data-media-id="${this.currentItem.dataset.mediaId}"]`
      );
      const btn = this.currentThumb.querySelector('button');

      this.thumbs.querySelectorAll('.media-thumbs__btn').forEach((el) => {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });

      btn.classList.add('is-active');
      btn.setAttribute('aria-current', 'true');
      this.checkThumbVisibilty(this.currentThumb);
    }

    /**
     * Creates an array of the visible media items.
     */
    setVisibleItems() {
      this.viewerItems = this.querySelectorAll('.media-viewer__item');
      this.visibleItems = Array.from(this.viewerItems).filter((el) => el.clientWidth > 0);

      // Fallback: if no items have width yet (e.g. during custom element construction
      // before layout), use items that aren't hidden by media grouping
      if (this.visibleItems.length === 0 && this.currentGroup) {
        this.visibleItems = Array.from(this.viewerItems).filter((el) => el.style.display !== 'none');
      }

      if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
    }

    /**
     * Ensures a thumbnail is in the visible area of the slider.
     * @param {Element} thumb - Thumb item element.
     */
    checkThumbVisibilty(thumb) {
      const scrollPos = this.thumbs.scrollLeft;
      const lastVisibleThumbOffset = this.thumbs.clientWidth + scrollPos;
      const thumbOffset = thumb.offsetLeft;

      if (thumbOffset + thumb.clientWidth > lastVisibleThumbOffset || thumbOffset < scrollPos) {
        this.thumbs.scrollTo({ left: thumbOffset, behavior: 'smooth' });
      }
    }

    /**
     * Updates the media gallery status.
     * @param {Element} mediaItem - Active media element.
     * @param {number} index - Active media index.
     */
    announceLiveRegion(mediaItem, index) {
      const image = mediaItem.querySelector('.media-viewer img');
      if (!image) return;

      this.liveRegion.setAttribute('aria-hidden', 'false');
      this.liveRegion.innerHTML = theme.strings.imageAvailable.replace('[index]', index);

      setTimeout(() => {
        this.liveRegion.setAttribute('aria-hidden', 'true');
      }, 2000);
    }

    /**
     * Loads the deferred media for the active item.
     * @param {Element} mediaItem - Active media element.
     */
    static playActiveMedia(mediaItem) {
      window.pauseAllMedia();
      const deferredMedia = mediaItem.querySelector('deferred-media');
      if (deferredMedia) deferredMedia.loadContent();
    }
  }

  customElements.define('media-gallery', MediaGallery);
}
